import type { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, firstValueFrom, map, mergeMap, of, switchMap, takeUntil } from 'rxjs';

import type { GroupsResponse } from '@recruitee/views-types';

import { ViewGroupsActions } from './groups.store';
import { ViewsHttpService } from '../../services/views-http.service';

@Injectable({ providedIn: 'root' })
export class GroupsEffects {
  public fetchOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewGroupsActions.fetch),
      mergeMap(({ payload: { storeGroupId, dataSourceId, data, queryParams } }) =>
        this.viewsHttpService.fetchGroups(dataSourceId, data, queryParams).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(ViewGroupsActions.fetch),
              filter(({ payload }) => payload.storeGroupId === storeGroupId),
            ),
          ),
          map(response =>
            ViewGroupsActions.fetchSuccess({
              payload: {
                data: { ...response, id: storeGroupId },
                concat: (queryParams.page ?? 0) > 1,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(ViewGroupsActions.fetchFailed({ payload: { error, id: storeGroupId } })),
          ),
        ),
      ),
    ),
  );

  public fetchAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewGroupsActions.fetchAll),
      mergeMap(({ payload: { storeGroupId, dataSourceId, data, uuid, searchQuery } }) =>
        of(null).pipe(
          switchMap(async () => {
            // TODO
            /**
             * This is a temporary solution to fetch all groups.
             * Backend changes are required to remove this workaround.
             */
            const limit = 1000;
            let page = 1;
            let allLoadel = false;
            let response: GroupsResponse | null = null;

            while (!allLoadel) {
              const res = await firstValueFrom(
                this.viewsHttpService.fetchGroups(dataSourceId, data, { page, limit, searchQuery }),
              );
              response = response ? { ...res, groups: [...response.groups, ...res.groups] } : res;
              if (response.meta.totalCount < limit * page) {
                allLoadel = true;
              } else {
                page = page + 1;
              }
            }

            return ViewGroupsActions.fetchAllSuccess({
              payload: { uuid, data: { ...response!, id: storeGroupId } },
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(ViewGroupsActions.fetchAllFailed({ payload: { error, id: storeGroupId } })),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private viewsHttpService: ViewsHttpService,
  ) {}
}
